import { CompensationMethodType } from "@redotech/congo-enums/compensation-method-type";
import { Payer } from "@redotech/congo-enums/payer";
import { Json } from "@redotech/json/json";
import { countries } from "@redotech/locale/countries";
import { territoryCodeToCountryMap } from "@redotech/locale/country-codes";
import { canadaProvinces, usaProvinces } from "@redotech/locale/provinces";
import { shouldChargeMerchantForDamagedOrWrongProduct } from "@redotech/redo-billing-common/damaged-products";
import { Provider } from "@redotech/redo-order-common/provider";
import { ProductStatus } from "@redotech/redo-product-common/product";
import { ShipmentMethodName } from "@redotech/redo-return-core-common/draft-return/draft-return-enums";
import {
  ExchangeVariant,
  PendingReturnItem,
  ReturnableItem,
  ReturnProduct,
  VariantExchangeItem,
} from "@redotech/redo-return-core-common/draft-return/draft-return-items";
import type { InventorySettings } from "@redotech/redo-return-core-common/inventory-settings";
import { MultipleChoiceTextAnswer } from "@redotech/redo-return-core-common/return";
import {
  ClaimOption,
  getTreatment,
  ReturnFlow,
  returnFlowJsonFormat,
  ReturnOption,
  Step,
} from "@redotech/redo-return-core-common/return-flow";
import {
  applyAgentReturnWindowGrace,
  getAgentReturnWindowDecision,
} from "@redotech/redo-return-core-common/return-flow/agent-return-window-flow";
import {
  isDamaged,
  isWrongProduct,
} from "@redotech/redo-return-core-common/return-flow/condition-predicates";
import { AgentTimePolicyTier } from "@redotech/redo-return-core-common/return-flow/evaluate-agent-time-policy";
import { ReturnOptionMethod } from "@redotech/redo-return-core-common/return-flow/return-option";
import { ReturnTypeEnum } from "@redotech/redo-return-core-common/return-type-enum";
import { OrderContact } from "@redotech/redo-return-core-common/third-party-refunds/recipient";
import { RepairFeeCollectionStrategy } from "@redotech/redo-return-warranties-common/warranties";
import { Big } from "big.js";
import { ObjectId } from "bson";
import {
  isSameItemVariantEligible,
  isVariantInStock,
} from "../returns/products/product-variant-utils";
import {
  CompensationMethod,
  CompensationType,
  DraftReturn,
  Order as DraftReturnOrder,
  DraftReturn as DraftReturnZod,
  PriceRange,
} from "./draft-return";

export interface CombinedReturnItem {
  returnableItem: ReturnableItem;
  pendingReturnItem: PendingReturnItem;
}

export const returnOptionMethodMap: {
  [key in CompensationType]: ReturnOptionMethod["type"];
} = {
  exchange: ReturnOptionMethod.EXCHANGE,
  storeCredit: ReturnOptionMethod.STORE_CREDIT,
  refund: ReturnOptionMethod.REFUND,
  repair: ReturnOptionMethod.REPAIR,
};

export enum ExchangeType {
  ADVANCED = "advanced",
  VARIANT = "variant",
  GROUP = "group",
}

export function getPreserveProperties({
  option,
  exchangeType,
}: {
  option: ReturnOption | ClaimOption | undefined;
  exchangeType: ExchangeType;
}): boolean {
  if (
    exchangeType === ExchangeType.ADVANCED ||
    exchangeType === ExchangeType.GROUP
  ) {
    return false;
  }
  return option?.carryOverLineItemProperties ?? true;
}

export function shouldGatePropertyCarryOverOnAddOnAccept(
  option: ReturnOption | ClaimOption | undefined,
): boolean {
  return (
    !!option?.variantExchangeAddOn &&
    !!option?.carryOverLineItemPropertiesOnAddOnAccept &&
    (option.carryOverLineItemProperties ?? true)
  );
}

export function acceptedVariantExchangeAddOn({
  option,
  advancedExchangeItems,
}: {
  option: ReturnOption | ClaimOption | undefined;
  advancedExchangeItems:
    | readonly { variantId?: string | null }[]
    | null
    | undefined;
}): boolean {
  const addOnVariantId = option?.variantExchangeAddOnVariantId;
  if (!addOnVariantId) {
    return false;
  }
  return (advancedExchangeItems ?? []).some(
    (item) => String(item.variantId) === String(addOnVariantId),
  );
}

export function getPreservePropertiesForVariantExchangeAddOn({
  option,
  acceptedAddOn,
}: {
  option: ReturnOption | ClaimOption | undefined;
  acceptedAddOn: boolean;
}): boolean {
  if (shouldGatePropertyCarryOverOnAddOnAccept(option)) {
    return acceptedAddOn;
  }
  return getPreserveProperties({ option, exchangeType: ExchangeType.VARIANT });
}

export function getCombinedReturnItem(
  draftReturn: DraftReturnZod,
  pendingItemId: string,
): CombinedReturnItem {
  const pendingReturnItem = draftReturn.pendingReturnItems.find(
    (pendingItem) => pendingItem.id.toString() === pendingItemId.toString(),
  );
  if (!pendingReturnItem) {
    throw new Error(`Pending item with id ${pendingItemId} not found`);
  }
  const returnableItem = draftReturn.returnableItems.find(
    (item) => item.id === pendingReturnItem?.returnableItemId,
  );
  if (!returnableItem) {
    throw new Error(
      `Returnable item with id ${pendingReturnItem?.returnableItemId} not found`,
    );
  }
  return { returnableItem, pendingReturnItem };
}

export function getCombinedReturnItems(
  draftReturn: DraftReturnZod,
): CombinedReturnItem[] {
  return draftReturn.pendingReturnItems.map((pendingItem) =>
    getCombinedReturnItem(draftReturn, pendingItem.id),
  );
}

export function getDummyCombinedReturnItems(
  returnableItems: ReturnableItem[],
): CombinedReturnItem[] {
  let itemsBeingReturned = returnableItems.filter(
    (returnableItem) => returnableItem.selected,
  );
  if (itemsBeingReturned.length === 0) {
    // If no items have been selected yet, return dummy items for all returnable items
    itemsBeingReturned = returnableItems;
  }
  return itemsBeingReturned.map((returnableItem) => ({
    returnableItem,
    pendingReturnItem: {
      id: new ObjectId().toString(),
      returnableItemId: returnableItem.id,
      flowPath: [],
      quantity: getPrimaryProduct(returnableItem).quantity,
      inputAnswers: [],
      multipleChoiceAnswers: [],
      returnReasons: [],
      reasonCodes: [],
      flagged: false,
      flow: undefined,
      manualReview: false,
      returnOptions: [],
    },
  }));
}

export const getPrimaryProduct = <
  P extends Pick<ReturnProduct, "id" | "isExpandedPrimaryProduct"> =
    ReturnProduct,
>(
  returnItem: Pick<ReturnableItem, "isExpanded" | "primaryProductId"> & {
    products: readonly P[];
  },
): P => {
  if (returnItem.isExpanded) {
    const primaryProduct = returnItem.products.find(
      (product) =>
        product.id === returnItem.primaryProductId &&
        product.isExpandedPrimaryProduct,
    );
    if (primaryProduct) {
      return primaryProduct;
    }
  }

  const primaryProduct = returnItem.products.find(
    (product) => product.id === returnItem.primaryProductId,
  );
  if (!primaryProduct) {
    throw new Error("Primary product not found");
  }
  return primaryProduct;
};

export const getNonPrimaryProducts = (
  returnItem: ReturnableItem,
): ReturnProduct[] => {
  if (
    returnItem.isExpanded &&
    returnItem.products.some((product) => product.isExpandedPrimaryProduct)
  ) {
    return returnItem.products.filter(
      (product) => !product.isExpandedPrimaryProduct,
    );
  } else {
    return returnItem.products.filter(
      (product) => product.id !== returnItem.primaryProductId,
    );
  }
};

export function getMultipleChoiceAnswers(
  multipleChoiceAnswers: readonly { step: number; answer: number }[],
  flow: ReturnFlow,
  flowPath: readonly number[],
): MultipleChoiceTextAnswer[] {
  const answers: MultipleChoiceTextAnswer[] = [];
  for (const stepIndex of flowPath) {
    const step = flow.steps[stepIndex];

    if (
      // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
      step.type.toString() === Step.MULTIPLE_CHOICE.description ||
      // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
      step.type.description === Step.MULTIPLE_CHOICE.description
    ) {
      const choice = multipleChoiceAnswers.find(
        (answer) => answer.step === stepIndex,
      );

      if (choice) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
        const option = (step as Step.MultipleChoice).options[choice.answer];
        if (option) {
          answers.push({
            answer: option.name,
            // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
            questionText: (step as Step.MultipleChoice).message,
            step: stepIndex,
          });
        }
      }
    }
  }
  return answers;
}

// Stored flows carry step and method types as strings (JSON) or symbols
// (parsed), so compare by description.
const symbolTypeMatches = (type: symbol, expected: symbol): boolean =>
  type === expected ||
  type.toString() === expected.description ||
  type.description === expected.description;

const stepHasType = (step: Step, type: symbol): boolean =>
  symbolTypeMatches(step.type, type);

function isSubmitClaimOrWarrantyStep(
  step: Step,
): step is Step.SubmitClaim | Step.SubmitWarranty {
  return (
    stepHasType(step, Step.SUBMIT_CLAIM) ||
    stepHasType(step, Step.SUBMIT_WARRANTY)
  );
}

/**
 * Stock context for judging a Return block's same-item replacement. Without it
 * a Return block item with no exchange attached is treated as having chosen
 * not to exchange, since the block also offers refund or credit.
 */
export interface SameItemStockContext {
  primaryProduct: Pick<ReturnProduct, "variantId" | "variantData">;
  inventorySettings: InventorySettings;
  returnType: ReturnTypeEnum | undefined;
}

/**
 * True when the item's submit step offered a same-item replacement but the
 * portal found the variant out of stock or deleted and attached no exchange
 * item, so the item value falls through to store credit or refund. For a
 * Return block, the same-item variant must be verifiably out of stock via
 * `stockContext`.
 */
export function isSameItemReplacementUnavailable(
  pendingReturnItem: Pick<
    PendingReturnItem,
    "flow" | "flowPath" | "variantExchangeItem" | "advancedExchangeItems"
  >,
  stockContext?: SameItemStockContext,
): boolean {
  if (
    pendingReturnItem.variantExchangeItem ||
    pendingReturnItem.advancedExchangeItems?.length
  ) {
    return false;
  }
  const flow: Partial<ReturnFlow> | undefined = pendingReturnItem.flow;
  const finalStepIndex =
    pendingReturnItem.flowPath[pendingReturnItem.flowPath.length - 1];
  const finalStep =
    finalStepIndex === undefined ? undefined : flow?.steps?.[finalStepIndex];
  if (!finalStep) {
    return false;
  }
  if (isSubmitClaimOrWarrantyStep(finalStep)) {
    return symbolTypeMatches(
      finalStep.option.method.type,
      ReturnOptionMethod.EXCHANGE,
    );
  }
  if (!stepHasType(finalStep, Step.RETURN) || !stockContext) {
    return false;
  }
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  const offersSameItemExchange = (finalStep as Step.Return).options.some(
    (option) =>
      symbolTypeMatches(option.method.type, ReturnOptionMethod.EXCHANGE) &&
      option.exchangeTypes?.sameItem,
  );
  if (!offersSameItemExchange) {
    return false;
  }
  return !isSameItemVariantInStock(stockContext);
}

function isSameItemVariantInStock({
  primaryProduct,
  inventorySettings,
  returnType,
}: SameItemStockContext): boolean {
  return primaryProduct.variantData.variants.some(
    (variant) =>
      String(variant.id) === String(primaryProduct.variantId) &&
      isSameItemVariantEligible({
        inventorySettings,
        returnType,
        productStatus: primaryProduct.variantData.status,
        inventoryPolicy: variant.inventoryPolicy,
        inventoryTracked: variant.inventoryTracked,
        inventoryQuantity: variant.inventoryQuantity,
      }),
  );
}

/**
 * The returned item's own variant if in stock, counting variants withheld from
 * sale (draft, archived, unpublished, metafield-excluded) but not deleted ones.
 */
export function getInStockSameItemVariantIncludingUnpublished(
  primaryProduct: Pick<ReturnProduct, "variantId" | "variantData">,
  inventorySettings: InventorySettings,
  returnType: ReturnTypeEnum | undefined,
): ExchangeVariant | undefined {
  const variant = findSameItemVariantIncludingUnpublished(primaryProduct);
  if (!variant) {
    return undefined;
  }
  return isVariantInStock({
    inventorySettings,
    returnType,
    inventoryPolicy: variant.inventoryPolicy,
    inventoryTracked: variant.inventoryTracked,
    inventoryQuantity: variant.inventoryQuantity,
  })
    ? variant
    : undefined;
}

function findSameItemVariantIncludingUnpublished({
  variantId,
  variantData,
}: Pick<ReturnProduct, "variantId" | "variantData">):
  | ExchangeVariant
  | undefined {
  const matchesReturnedItem = (variant: ExchangeVariant) =>
    String(variant.id) === String(variantId);
  return (
    variantData.variants.find(matchesReturnedItem) ??
    variantData.unpublishedVariants?.find(matchesReturnedItem)
  );
}

/**
 * Providers whose product lookup returns an empty variant list when the
 * product is gone. Every other provider returns an empty list unconditionally,
 * so absence carries no signal there.
 */
export const PRODUCT_AVAILABILITY_PROVIDERS: ReadonlySet<Provider> = new Set([
  Provider.SHOPIFY,
  Provider.BIGCOMMERCE,
  Provider.COMMERCE_CLOUD,
]);

/**
 * Whether the returned item's product still exists on the store and its
 * original variant is purchasable, so a same-item exchange could be fulfilled.
 * Backs the PRODUCT_AVAILABLE condition in the pre-option flows.
 *
 * Deleted products and variants hidden by the unpublished filter have no
 * matching variant; archived products and (when inventory settings are
 * provided) out-of-stock variants fail the same stock check the exchange
 * picker uses. Unsupported providers always report available so the condition
 * never diverts a flow on data that cannot distinguish "gone" from "unknown".
 *
 * With `allowUnpublished`, product status and publication are ignored the same
 * way `getInStockSameItemVariantIncludingUnpublished` ignores them, leaving only
 * deleted and out-of-stock items unavailable.
 */
export function isReturnedProductAvailable({
  primaryProduct,
  provider,
  inventorySettings,
  returnType,
  allowUnpublished = false,
}: {
  primaryProduct: Pick<ReturnProduct, "variantId" | "variantData">;
  provider: Provider;
  inventorySettings: InventorySettings | undefined;
  returnType: ReturnTypeEnum | undefined;
  allowUnpublished?: boolean;
}): boolean {
  if (
    !PRODUCT_AVAILABILITY_PROVIDERS.has(provider) ||
    !primaryProduct.variantData
  ) {
    return true;
  }
  if (allowUnpublished) {
    return inventorySettings
      ? !!getInStockSameItemVariantIncludingUnpublished(
          primaryProduct,
          inventorySettings,
          returnType,
        )
      : !!findSameItemVariantIncludingUnpublished(primaryProduct);
  }
  if (inventorySettings) {
    return isSameItemVariantInStock({
      primaryProduct,
      inventorySettings,
      returnType,
    });
  }
  const { status, variants } = primaryProduct.variantData;
  if (status?.toLowerCase() === ProductStatus.ARCHIVED) {
    return false;
  }
  return variants.some(
    (variant) => String(variant.id) === String(primaryProduct.variantId),
  );
}

/** The compensation the draft is priced on: the only one, or the selected one. */
export function selectCompensationMethod<T extends { type: CompensationType }>(
  compensationMethods: readonly T[],
  selectedMethod: CompensationType | null | undefined,
): T | undefined {
  return compensationMethods?.length === 1
    ? compensationMethods[0]
    : compensationMethods.find((method) => method.type === selectedMethod);
}

export function getCompensationMethod(
  draftReturn: Pick<DraftReturnZod, "compensationMethods" | "selectedMethod">,
): CompensationMethod | undefined {
  return selectCompensationMethod(
    draftReturn.compensationMethods,
    draftReturn.selectedMethod,
  );
}

export function getPriceAdjustmentForPendingItem(
  compensationMethod: CompensationMethod,
  pendingItem: PendingReturnItem,
) {
  return compensationMethod.adjustments[pendingItem.id.toString()]
    ? // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
      compensationMethod.adjustments[pendingItem.id.toString()]
        .div(pendingItem?.quantity || 1)
        .toFixed(2)
    : null;
}

export function compensationMethodHasRepair(
  compensationMethod: CompensationMethod,
) {
  return Object.values(compensationMethod.itemValues).some(
    (item) => item.strategy === "repair",
  );
}

export function hasReturnItemsThatRequireShipping(
  draftReturn: DraftReturnZod,
): boolean {
  return Object.values(
    getCompensationMethod(draftReturn)?.shippingRequired ?? {},
  ).some(Boolean);
}

export function getShipmentMethod(
  draftReturn: DraftReturnZod,
): ShipmentMethodName {
  return draftReturn?.shipmentMethod?.method ?? ShipmentMethodName.SELF_SHIP;
}

export function getBaseRepairFee(repairFee: PriceRange): number {
  return repairFee[0];
}

/** The per-item repair fee data needed to total a return's repair fees. */
export interface RepairFeeItem {
  repairFee?: Step.RepairFeeConfig | null;
  /** Merchant-override gate: `false` disables the item's fee entirely. */
  chargeRepairFee?: boolean | null;
  /** Basis for percentage fees: pre-discount price × returned quantity. */
  preDiscountPrice?: Big | null;
}

/**
 * Percentage fees resolve against the pre-discount price, rounded to cents;
 * a missing price basis resolves to 0.
 */
function resolveRepairFeeAmount(
  value: number,
  amountType: Step.RepairFeeAmountType | undefined,
  preDiscountPrice: Big | null | undefined,
): number {
  if (amountType !== Step.RepairFeeAmountType.Percentage) {
    return value;
  }
  return Number(
    Big(preDiscountPrice ?? 0)
      .times(value)
      .div(100)
      .round(2),
  );
}

export interface RepairFeeTotals {
  /**
   * Undefined when no item carries a fee. Deferred wins when any deferred
   * item exists or the return is merchant-created (no card to charge).
   */
  strategy: RepairFeeCollectionStrategy | undefined;
  /**
   * [min, max] fee estimate; instant-only ⇒ both bounds are the summed
   * amounts. Index 0 is what's charged/invoiced — see getBaseRepairFee.
   */
  range: PriceRange;
}

/**
 * Totals per-item repair fee configs from the flow's SubmitRepair blocks.
 * Free items (or items disabled by merchant override) contribute nothing.
 */
export function computeRepairFeeTotals(
  items: RepairFeeItem[],
  { merchantCreated }: { merchantCreated: boolean },
): RepairFeeTotals {
  let instantTotal = 0;
  let deferredMin = 0;
  let deferredMax = 0;
  let hasDeferred = false;
  let hasInstant = false;

  for (const item of items) {
    if (item.chargeRepairFee === false || !item.repairFee) {
      continue;
    }
    switch (item.repairFee.strategy) {
      case Step.RepairFeeMode.Instant:
        hasInstant = true;
        instantTotal += resolveRepairFeeAmount(
          item.repairFee.amount,
          item.repairFee.amountType,
          item.preDiscountPrice,
        );
        break;
      case Step.RepairFeeMode.Deferred:
        hasDeferred = true;
        deferredMin += resolveRepairFeeAmount(
          item.repairFee.minPrice,
          item.repairFee.amountType,
          item.preDiscountPrice,
        );
        deferredMax += resolveRepairFeeAmount(
          item.repairFee.maxPrice,
          item.repairFee.amountType,
          item.preDiscountPrice,
        );
        break;
      case Step.RepairFeeMode.Free:
        break;
    }
  }

  if (!hasDeferred && !hasInstant) {
    return { strategy: undefined, range: [0, 0] };
  }
  if (hasDeferred || merchantCreated) {
    return {
      strategy: RepairFeeCollectionStrategy.DEFERRED,
      range: [deferredMin + instantTotal, deferredMax + instantTotal],
    };
  }
  return {
    strategy: RepairFeeCollectionStrategy.INSTANT,
    range: [instantTotal, instantTotal],
  };
}

/**
 * Derives the persisted collection strategy from the repair compensation
 * method's per-item fee configs (replaces the legacy team-level setting).
 */
export function deriveRepairFeeCollectionStrategy(
  compensationMethods: readonly {
    type: CompensationMethod["type"];
    itemValues: Record<string, RepairFeeItem>;
  }[],
  merchantCreated: boolean,
): RepairFeeCollectionStrategy | undefined {
  const repairMethod = compensationMethods.find(
    (method) => method.type === CompensationMethodType.Repair,
  );
  if (!repairMethod) {
    return undefined;
  }
  return computeRepairFeeTotals(Object.values(repairMethod.itemValues), {
    merchantCreated,
  }).strategy;
}

export const getReturnableItemTotalReturnValue = (
  returnItem: ReturnableItem | null,
  pendingItemQuantity?: number | null,
): Big => {
  if (!returnItem) {
    return Big(0);
  }
  return (returnItem?.products || []).reduce((acc, product) => {
    if (!returnItem?.isExpanded || product?.isExpandedPrimaryProduct) {
      return acc.plus(product.returnValue).times(pendingItemQuantity ?? 1);
    }
    return acc;
  }, Big(0));
};

export const getReturnableItemTotalTax = (
  returnItem: ReturnableItem | null,
  pendingItem: PendingReturnItem | null,
): Big => {
  return (returnItem?.products || []).reduce((acc, product) => {
    if (!returnItem?.isExpanded || product.isExpandedPrimaryProduct) {
      return acc.plus(product.tax).times(pendingItem?.quantity ?? 1);
    }
    return acc;
  }, Big(0));
};

export const getReturnableItemTotalPrice = (
  returnItem: ReturnableItem | null,
  pendingItem: PendingReturnItem | null,
): Big => {
  return (returnItem?.products || []).reduce((acc, product) => {
    if (!returnItem?.isExpanded || product?.isExpandedPrimaryProduct) {
      return acc
        .plus(product?.originalPrice ?? product.price)
        .times(pendingItem?.quantity ?? 1);
    }
    return acc;
  }, Big(0));
};

/**
 * Tolerant {@link getReturnableItemTotalPrice} for fee bases: null (instead
 * of a Big parse throw) when an item has no usable price.
 */
export const getReturnableItemPreDiscountPrice = (
  returnItem: ReturnableItem | null,
  pendingItem: PendingReturnItem | null,
): Big | null => {
  try {
    return getReturnableItemTotalPrice(returnItem, pendingItem);
  } catch {
    return null;
  }
};

export const getReturnableItemTotalGrams = (
  returnItem: ReturnableItem | null,
  pendingItem: PendingReturnItem | null,
): Big => {
  return (returnItem?.products || []).reduce(
    (acc, item) => acc.plus(item.grams).times(pendingItem?.quantity ?? 1),
    Big(0),
  );
};

export const isVariantExchange = (pendingItem: PendingReturnItem) =>
  (!!pendingItem.variantExchangeItem &&
    pendingItem.variantExchangeItem.priceDifference !== "apply") ||
  (pendingItem?.expandedProductVariantExchanges || []).length > 0;

/** Includes different-priced variant exchanges that are treated as advanced. */
export const isAnyVariantExchange = (pendingItem: PendingReturnItem) =>
  !!pendingItem.variantExchangeItem || isVariantExchange(pendingItem);

export const isPreBonusUpsell = (pendingItem: PendingReturnItem) =>
  !!pendingItem &&
  !isVariantExchange(pendingItem) &&
  !!pendingItem.advancedExchangeItems?.length;

export function getTotalReturnableValue(items: CombinedReturnItem[]): Big {
  return items.reduce(
    (acc, { pendingReturnItem, returnableItem }) =>
      acc
        .plus(
          getReturnableItemTotalReturnValue(
            returnableItem,
            pendingReturnItem.quantity,
          ),
        )
        .plus(getReturnableItemTotalTax(returnableItem, pendingReturnItem)),
    Big(0),
  );
}

export function isPrimaryProductCoveredByFinalSale(
  primaryItem: Pick<ReturnProduct, "productId">,
  order:
    | Pick<
        DraftReturnOrder,
        "finalSaleReturnsProtected" | "productsCoveredByFinalSale"
      >
    | undefined,
): boolean {
  if (!order) {
    return false;
  }

  return (
    order.finalSaleReturnsProtected &&
    order.productsCoveredByFinalSale.includes(primaryItem.productId)
  );
}

export function getTestAndTreatment(draftReturn: DraftReturnZod): {
  testId: string | undefined;
  treatmentId: string | undefined;
} {
  for (const pendingItem of draftReturn.pendingReturnItems) {
    if (pendingItem?.flow) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
      const flow = pendingItem.flow as ReturnFlow;
      for (const pathIndex of pendingItem.flowPath) {
        if (
          // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
          // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
          (flow.steps[pathIndex].type as unknown as string) ===
          Step.AB_TEST.description
        ) {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
          const testStep = flow.steps[pathIndex] as Step.ABTest;
          const treatment = getTreatment(
            testStep?.treatments || [],
            draftReturn.treatmentHash,
          );
          if (testStep && treatment) {
            return {
              testId: standardizeName(testStep.name),
              treatmentId: standardizeName(treatment?.name),
            };
          }
        }
      }
    }
  }
  return { testId: undefined, treatmentId: undefined };
}

export function standardizeName(name: string) {
  return name.trim().toLowerCase().replace(/\s+/g, "_");
}

export function getReturnOrders(
  draftReturn: DraftReturnZod,
): Record<string, DraftReturnOrder> {
  return draftReturn.pendingReturnItems.reduce<
    Record<string, DraftReturnOrder>
  >((acc: Record<string, DraftReturnOrder>, pendingItem) => {
    const combinedItem = getCombinedReturnItem(draftReturn, pendingItem.id);
    const order = draftReturn.orders.find(
      (order) => order.id === combinedItem.returnableItem.orderId,
    );
    if (order && !acc[order.id]) {
      acc[order.id] = order;
    }
    return acc;
  }, {});
}

/**
 * Get the country code for a given country name.
 *
 * @param name - The name of the country.
 * @param resetTerritoryCodeToCountryCode - Whether to reset the territory code to the country code. see: https://shopify.dev/docs/api/admin-graphql/latest/enums/CountryCode
 * @returns The country code.
 */
export function getCountryCode(
  name: string | null | undefined,
  resetTerritoryCodeToCountryCode = false,
): string | undefined {
  if (!name) {
    return undefined;
  }

  const code = countries.find(
    (country) =>
      country.name.toLowerCase() === name.toLowerCase() ||
      country.code.toLowerCase() === name.toLowerCase(),
  )?.code;

  if (
    code &&
    resetTerritoryCodeToCountryCode &&
    territoryCodeToCountryMap.has(code)
  ) {
    return territoryCodeToCountryMap.get(code) ?? code;
  }

  return code;
}

export function getProvinceCode(
  name: string | null | undefined,
  countryCode: string,
): string | undefined {
  if (!name) {
    return undefined;
  }

  const provinces =
    countryCode === "CA"
      ? canadaProvinces
      : countryCode === "US"
        ? usaProvinces
        : [];

  const nameLower = name.toLowerCase();
  return provinces.find(
    (province) =>
      province.name.toLowerCase() === nameLower ||
      province.code.toLowerCase() === nameLower,
  )?.code;
}

export function getCountryName(
  code: string | null | undefined,
): string | undefined {
  if (!code) {
    return undefined;
  }

  return countries.find(
    (country) =>
      country.name.toLowerCase() === code.toLowerCase() ||
      country.code.toLowerCase() === code.toLowerCase(),
  )?.name;
}

export const getExpandedVariantExchangeProduct = (
  returnableItem: ReturnableItem,
  productId: string | null | undefined,
): ReturnProduct | undefined => {
  if (returnableItem.products.length === 1) {
    return returnableItem.products[0];
  }
  return returnableItem.products.find(
    (product) => product.productId === productId,
  );
};

export const getExpandedReturnItemProducts = (
  combinedItem: CombinedReturnItem,
): ReturnProduct[] => {
  const products: (ReturnProduct | undefined)[] = [];
  if (
    combinedItem.pendingReturnItem?.expandedProductVariantExchanges &&
    (combinedItem.pendingReturnItem.expandedProductVariantExchanges || [])
      .length > 0
  ) {
    for (const variantExchange of combinedItem.pendingReturnItem
      .expandedProductVariantExchanges) {
      products.push(
        getExpandedVariantExchangeProduct(
          combinedItem.returnableItem,
          variantExchange.productId,
        ),
      );
    }
  } else {
    products.push(getPrimaryProduct(combinedItem.returnableItem));
  }
  return products.filter((product) => !!product);
};

export const getExpandedReturnItemVariantExchanges = (
  combinedItem: CombinedReturnItem,
): VariantExchangeItem[] => {
  if (
    combinedItem.pendingReturnItem?.expandedProductVariantExchanges &&
    (combinedItem.pendingReturnItem.expandedProductVariantExchanges || [])
      .length > 0
  ) {
    return combinedItem.pendingReturnItem.expandedProductVariantExchanges;
  } else if (combinedItem.pendingReturnItem?.variantExchangeItem) {
    return [combinedItem.pendingReturnItem.variantExchangeItem];
  } else {
    return [];
  }
};

export const getVariantExchangeItemsAndProducts = (
  draftReturn: DraftReturnZod,
): {
  variant: VariantExchangeItem;
  product: ReturnProduct | undefined;
  quantity: number;
}[] => {
  const variantExchangeItems: {
    variant: VariantExchangeItem;
    product: ReturnProduct | undefined;
    quantity: number;
  }[] = [];
  const compensationMethod = getCompensationMethod(draftReturn);
  if (!compensationMethod) {
    return variantExchangeItems;
  }

  for (const [id, item] of Object.entries(compensationMethod.itemValues)) {
    if (!item.isVariantExchange) {
      continue;
    }

    const combinedItem = getCombinedReturnItem(draftReturn, id);
    if (combinedItem.returnableItem?.isExpanded) {
      const expandedVariantExchanges =
        getExpandedReturnItemVariantExchanges(combinedItem);

      for (const variantExchange of expandedVariantExchanges) {
        variantExchangeItems.push({
          variant: variantExchange,
          product: getExpandedVariantExchangeProduct(
            combinedItem.returnableItem,
            variantExchange.productId,
          ),
          quantity: combinedItem.pendingReturnItem?.quantity ?? 1,
        });
      }
    } else if (combinedItem.pendingReturnItem?.variantExchangeItem) {
      variantExchangeItems.push({
        variant: combinedItem.pendingReturnItem?.variantExchangeItem,
        product: getPrimaryProduct(combinedItem.returnableItem),
        quantity: combinedItem.pendingReturnItem?.quantity ?? 1,
      });
    }
  }

  return variantExchangeItems;
};

export interface ReturnProductItem {
  product: ReturnProduct;
  pendingItem: PendingReturnItem;
}

export const getReturnShippingProducts = (
  draftReturn: DraftReturnZod,
): ReturnProductItem[] => {
  const products: ReturnProductItem[] = [];
  const compensationMethod = getCompensationMethod(draftReturn);
  if (!compensationMethod) {
    return products;
  }

  for (const id of Object.keys(compensationMethod.itemValues)) {
    if (!compensationMethod.shippingRequired[id]) {
      continue;
    }

    const combinedItem = getCombinedReturnItem(draftReturn, id);
    if (combinedItem.returnableItem?.isExpanded) {
      const expandedProducts = getExpandedReturnItemProducts(combinedItem);
      products.push(
        ...expandedProducts.map((product) => ({
          product,
          pendingItem: combinedItem.pendingReturnItem,
        })),
      );
    } else {
      for (const product of combinedItem.returnableItem.products) {
        products.push({ product, pendingItem: combinedItem.pendingReturnItem });
      }
    }
  }

  return products.filter((product) => !!product);
};

export const getReturnNonShippingProducts = (
  draftReturn: DraftReturnZod,
): ReturnProductItem[] => {
  const products: ReturnProductItem[] = [];
  const compensationMethod = getCompensationMethod(draftReturn);
  if (!compensationMethod) {
    return products;
  }

  for (const id of Object.keys(compensationMethod.itemValues)) {
    if (compensationMethod.shippingRequired[id]) {
      continue;
    }

    const combinedItem = getCombinedReturnItem(draftReturn, id);
    if (combinedItem.returnableItem?.isExpanded) {
      const expandedProducts = getExpandedReturnItemProducts(combinedItem);
      products.push(
        ...expandedProducts.map((product) => ({
          product,
          pendingItem: combinedItem.pendingReturnItem,
        })),
      );
    } else {
      for (const product of combinedItem.returnableItem.products) {
        products.push({ product, pendingItem: combinedItem.pendingReturnItem });
      }
    }
  }

  return products;
};

export const getExpandedProductVariantExchangeItem = (
  pendingReturnItem: PendingReturnItem,
  productId: string,
): VariantExchangeItem | undefined => {
  if (pendingReturnItem?.variantExchangeItem) {
    return pendingReturnItem.variantExchangeItem;
  }

  return pendingReturnItem.expandedProductVariantExchanges?.find(
    (exchange) => exchange.productId === productId,
  );
};

export const getRelevantOrders = (
  draftReturn: DraftReturnZod,
): DraftReturnOrder[] => {
  return draftReturn.pendingReturnItems
    .map((item) => {
      const combinedItem = getCombinedReturnItem(draftReturn, item.id);
      return draftReturn.orders.find(
        (order) => order.id === combinedItem.returnableItem.orderId,
      );
    })
    .filter((order) => !!order);
};

export const getRelevantDisplayDiscountCodes = (
  draftReturn: DraftReturnZod,
): string[] => {
  return Array.from(
    new Set(
      getRelevantOrders(draftReturn).flatMap((order) =>
        order.discounts.map((d) => (d.code || d.title).replace(/-RETURN/g, "")),
      ),
    ),
  ).filter((code) => code && code !== "Exchange Credit");
};

/** The returnable item fields that choose an item's return flow. */
export interface ReturnableItemFlowSource {
  id: string;
  primaryProductId: string;
  products: readonly {
    id: string;
    reset?: Pick<NonNullable<ReturnProduct["reset"]>, "returnFlow"> | null;
  }[];
}

/** The draft return fields that choose an item's return flow. */
export type DraftReturnFlowSource = Pick<
  DraftReturnZod,
  "flow" | "commerceAgentCreated" | "returnType" | "agentReturnWindow"
>;

export const getOriginalReturnableItemFlow = (
  returnableItem: ReturnableItemFlowSource | undefined,
  draftReturn: Pick<DraftReturnZod, "flow"> | undefined,
): Json => {
  const product = returnableItem?.products.find(
    (p) => p.id === returnableItem?.primaryProductId,
  );
  return product?.reset?.returnFlow &&
    product.reset.returnFlow.type === draftReturn?.flow?.type
    ? {
        steps: JSON.parse(JSON.stringify(product.reset.returnFlow.steps)),
        type: product.reset.returnFlow.type,
      }
    : draftReturn?.flow;
};

export const getReturnableItemFlow = (
  returnableItem: ReturnableItemFlowSource | undefined,
  draftReturn: DraftReturnFlowSource | undefined,
): Json => {
  const original = getOriginalReturnableItemFlow(returnableItem, draftReturn);
  if (
    !returnableItem ||
    !draftReturn?.commerceAgentCreated ||
    draftReturn.returnType !== ReturnTypeEnum.RETURN ||
    !draftReturn.agentReturnWindow
  ) {
    return original;
  }
  return returnFlowJsonFormat.write(
    applyAgentReturnWindowGrace(
      returnFlowJsonFormat.read(original),
      returnableItem.id,
      draftReturn.agentReturnWindow,
    ),
  );
};

export const shouldRemoveMerchantCreatedUpCharge = ({
  deductLabelFromCredit,
  dontChargeMerchantForDamagedOrWrongProductReturnLabels,
  removeMerchantPaidLabelUpcharge,
  draftReturn,
  isFlatRate,
  compensationMethod,
}: {
  deductLabelFromCredit?: boolean;
  dontChargeMerchantForDamagedOrWrongProductReturnLabels?: boolean | null;
  removeMerchantPaidLabelUpcharge?: boolean | null;
  draftReturn: DraftReturn;
  isFlatRate: boolean;
  compensationMethod?: CompensationMethod | null;
}): boolean => {
  if (!removeMerchantPaidLabelUpcharge) {
    return false;
  }

  if (compensationMethod?.payer === Payer.Merchant) {
    return true;
  }

  const isFlatRateShipping =
    compensationMethod?.payer !== Payer.Redo && isFlatRate;

  if (isFlatRateShipping) {
    return true;
  }

  const wouldDeductFromCredit =
    compensationMethod?.type === CompensationMethodType.Storecredit &&
    compensationMethod?.payer !== Payer.Redo &&
    deductLabelFromCredit;

  if (wouldDeductFromCredit) {
    return true;
  }

  return shouldChargeMerchantForDamagedOrWrongProduct({
    damaged: draftReturn.pendingReturnItems.some((p) =>
      isDamaged({
        returnReason: p.returnReason,
        aiDamaged: !!p.aiDamaged,
        isAiReason: !!p.aiReturnReasonsThreadId,
      }),
    ),
    wrongProduct: draftReturn.pendingReturnItems.some((p) =>
      isWrongProduct({
        returnReason: p.returnReason,
        aiWrongProduct: !!p.aiWrongProduct,
        isAiReason: !!p.aiReturnReasonsThreadId,
      }),
    ),
    dontChargeMerchantForDamagedOrWrongProductReturnLabels,
  });
};

export function allOrdersArePaidWithCash(draftReturn: {
  orders: readonly { paymentMethods: readonly string[] }[];
}): boolean {
  return draftReturn.orders.every((order) => {
    if (order.paymentMethods.length === 0) {
      return false;
    }
    return order.paymentMethods.every(
      (method) => method.toLowerCase().trim() === "cash",
    );
  });
}

/**
 * Groups one line's expanded children into pack instances.
 * Example: qty 2 of a 3-pack of A → two instances of 3, not six of 1.
 */
export function groupIntoBundleInstances(
  items: ReturnableItem[],
): ReturnableItem[][] {
  const firstItem = items[0];
  if (!firstItem) {
    return [items];
  }

  const childData = getPrimaryProduct(firstItem).bundleData?.childData;
  const packSize = childData?.length ?? 0;
  const distinctVariants = new Set(
    items.map((i) => getPrimaryProduct(i).variantId),
  ).size;
  const chunkSize = Math.max(packSize || distinctVariants, 1);

  if (items.length <= chunkSize) {
    return [items];
  }

  const perInstanceByVariant = new Map<string, number>();
  if (childData && childData.length > 0) {
    for (const child of childData) {
      perInstanceByVariant.set(
        child.variantId,
        (perInstanceByVariant.get(child.variantId) ?? 0) + 1,
      );
    }
  } else {
    for (const item of items) {
      const variantId = getPrimaryProduct(item).variantId;
      if (!perInstanceByVariant.has(variantId)) {
        perInstanceByVariant.set(variantId, 1);
      }
    }
  }

  const byVariant = new Map<string, ReturnableItem[]>();
  for (const item of items) {
    const variantId = getPrimaryProduct(item).variantId;
    let group = byVariant.get(variantId);
    if (!group) {
      group = [];
      byVariant.set(variantId, group);
    }
    group.push(item);
  }

  const instanceCount = Math.max(
    ...Array.from(byVariant.entries()).map(([variantId, group]) => {
      const perInstance = perInstanceByVariant.get(variantId) ?? 1;
      return Math.ceil(group.length / perInstance);
    }),
  );

  const instances: ReturnableItem[][] = [];
  const cursors = new Map<string, number>();
  for (let i = 0; i < instanceCount; i++) {
    const instance: ReturnableItem[] = [];
    for (const [variantId, group] of byVariant) {
      const perInstance = perInstanceByVariant.get(variantId) ?? 1;
      const start = cursors.get(variantId) ?? 0;
      const slice = group.slice(start, start + perInstance);
      instance.push(...slice);
      cursors.set(variantId, start + slice.length);
    }
    if (instance.length > 0) {
      instances.push(instance);
    }
  }

  return instances;
}

/** Contact info a refund-destination recipient is cross-checked against. */
export function orderContactFromDraftReturn(draftReturn: {
  customer: {
    updatedEmail?: string | null;
    shippingAddress?: { email?: string | null; phone?: string | null } | null;
  };
}): OrderContact {
  return {
    email:
      draftReturn.customer.updatedEmail ??
      draftReturn.customer.shippingAddress?.email,
    phone: draftReturn.customer.shippingAddress?.phone,
  };
}

export function getAgentReturnWindowQuoteDecisions(draft: DraftReturnZod) {
  const context = draft.agentReturnWindow;
  if (!draft.commerceAgentCreated || !context) {
    return [];
  }
  return draft.pendingReturnItems.flatMap((pending) => {
    const item = draft.returnableItems.find(
      (candidate) => candidate.id === pending.returnableItemId,
    );
    if (!item) {
      return [];
    }
    const original = returnFlowJsonFormat.read(
      getOriginalReturnableItemFlow(item, draft),
    );
    return pending.flowPath.flatMap((step) => {
      const decision = getAgentReturnWindowDecision(
        original,
        step,
        item.id,
        context,
      );
      if (
        decision?.kind !== "evaluated" ||
        decision.evaluation.tier === AgentTimePolicyTier.WITHIN_POLICY
      ) {
        return [];
      }
      return [
        {
          itemId: item.id,
          tier: decision.evaluation.tier,
          basis: decision.basis,
          policyDeadline: decision.evaluation.policyDeadline.toString(),
          merchantGraceDeadline:
            decision.evaluation.merchantGraceDeadline.toString(),
        },
      ];
    });
  });
}

export function requiresAgentReturnWindowMerchantReview(
  draft: DraftReturnZod,
): boolean {
  return getAgentReturnWindowQuoteDecisions(draft).some(
    (decision) => decision.tier === AgentTimePolicyTier.MERCHANT_GRACE,
  );
}

/** Retain the assessment with the item even after portal/payment continuation. */
export function getAgentReturnWindowReviewReason(
  draft: DraftReturnZod,
  itemId: string,
): string | undefined {
  const review = draft.agentReturnWindow?.review;
  if (!review) {
    return undefined;
  }
  const decision = getAgentReturnWindowQuoteDecisions(draft).find(
    (candidate) =>
      candidate.itemId === itemId &&
      candidate.tier === AgentTimePolicyTier.MERCHANT_GRACE,
  );
  if (!decision) {
    return undefined;
  }
  return [
    `Commerce Agent return-window exception (${decision.basis}): policy deadline ${decision.policyDeadline}; requested ${draft.agentReturnWindow?.evaluatedAt}; merchant review cutoff ${decision.merchantGraceDeadline}.`,
    review.customerExplanation,
    review.rationale,
  ].join("\n\n");
}
